import { createReducer, on } from '@ngrx/store';
import {
  FlattenedTreeModel,
  Group,
  OrganisationGroupModel,
  OrganizationState,
  User,
  ValidationStatusEnum,
} from 'app/shared/models';
import * as OrganizationActions from '../actions/organization.actions';

export const initialState: OrganizationState = {
  organizationTableLoading: false,
  userTableLoading: false,
  userAttributesLoading: false,
  treeTableLoading: false,
  loading: false,
  indicatorsLoading: [],
  deletedGroupsTableLoading: false,
  deletedGroupsCount: 0,
  orphanGroupsTableLoading: false,
  orphanGroupsCount: 0,
  users: {},
  managersUsers: [],
  selectedUserIds: [],
  tree: [],
  groupIdsAndUserIds: {},
  companyAttributes: [],
  error: null,
  errorTree: null,
  activeHierarchy: 0,
  validationStatus: ValidationStatusEnum.idle,
  fields: [],
  allowedMoveCategories: [],
  selectedGroupRows: [],
  selectedRows: [],
  activeSwitchOption: 'all',
  activeInstance: null,
  instanceStates: {},
};

export const reducer = createReducer(
  initialState,
  on(OrganizationActions.OrganizationTreeTableLoading, (state) => ({
    ...state,
    organizationTableLoading: true,
  })),
  on(OrganizationActions.OrganizationTreeTableLoadingComplete, (state) => ({
    ...state,
    organizationTableLoading: false,
  })),

  on(OrganizationActions.UserTableLoading, (state) => ({
    ...state,
    userTableLoading: true,
  })),
  on(OrganizationActions.UserTableLoadingComplete, (state) => ({
    ...state,
    userTableLoading: false,
  })),

  on(OrganizationActions.DeletedGroupsTableLoading, (state) => ({
    ...state,
    deletedGroupsTableLoading: true,
  })),
  on(OrganizationActions.DeletedGroupsTableLoadingComplete, (state) => ({
    ...state,
    deletedGroupsTableLoading: false,
  })),
  on(OrganizationActions.SetDeletedGroupsCount, (state, { payload }) => ({
    ...state,
    deletedGroupsCount: payload,
  })),

  on(OrganizationActions.OrphanGroupsTableLoading, (state) => ({
    ...state,
    orphanGroupsTableLoading: true,
  })),
  on(OrganizationActions.OrphanGroupsTableLoadingComplete, (state) => ({
    ...state,
    orphanGroupsTableLoading: false,
  })),
  on(OrganizationActions.SetOrphanGroupsCount, (state, { payload }) => ({
    ...state,
    orphanGroupsCount: payload,
  })),

  on(OrganizationActions.fetchGroupUsers, (state) => ({
    ...state,
    userTableLoading: true,
    error: null,
  })),
  on(OrganizationActions.fetchUsers, (state) => ({
    ...state,
    userTableLoading: true,
    userAttributesLoading: true,
    error: null,
  })),
  on(OrganizationActions.fetchUsersSuccess, (state, { users }) => {
    const updatedUsers = { ...state.users };

    users.forEach((user) => {
      updatedUsers[user.id] = user;
    });

    return {
      ...state,
      userTableLoading: false,
      users: updatedUsers,
      error: null,
    };
  }),
  on(OrganizationActions.fetchUsersError, (state, { error }) => ({
    ...state,
    userTableLoading: false,
    userAttributesLoading: false,
    users: {},
    error: error,
  })),

  on(OrganizationActions.fetchUserAttributesSuccess, (state, { userAttributes }) => {
    const users = { ...state.users };
    for (const userAttribute of userAttributes) {
      const user = users[userAttribute.userId];
      if (user) {
        user.userAttributes = userAttribute.attributes;
      }
    }

    return {
      ...state,
      userAttributesLoading: false,
      users: users,
      error: null,
    };
  }),

  on(OrganizationActions.fetchManagersUsers, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(OrganizationActions.fetchManagersUsersSuccess, (state, { users }) => ({
    ...state,
    loading: false,
    error: null,
    managersUsers: users,
  })),
  on(OrganizationActions.fetchManagersUsersError, (state, { error }) => ({
    ...state,
    loading: false,
    managersUsers: [],
    error: error,
  })),

  on(OrganizationActions.selectUsers, (state, { instanceId, ids }) => ({
    ...state,
    instanceStates: {
      ...state.instanceStates,
      [instanceId]: {
        ...state.instanceStates[instanceId],
        selectedUserIds: [
          ...new Set([...(state.instanceStates[instanceId]?.selectedUserIds || []), ...ids]),
        ],
      },
    },
  })),
  on(OrganizationActions.deselectUsers, (state, { instanceId, ids }) => ({
    ...state,
    instanceStates: {
      ...state.instanceStates,
      [instanceId]: {
        ...state.instanceStates[instanceId],
        selectedUserIds: !ids.length
          ? []
          : (state.instanceStates[instanceId]?.selectedUserIds || []).filter(
              (id) => !ids.includes(id),
            ),
      },
    },
  })),

  on(OrganizationActions.fetchTree, (state) => ({
    ...state,
    treeTableLoading: true,
    errorTree: null,
  })),
  on(OrganizationActions.fetchTreeSuccess, (state, { tree }) => ({
    ...state,
    treeTableLoading: false,
    loading: false,
    indicatorsLoading: [],
    selectedUserIds: [],
    tree: tree,
    errorTree: null,
  })),
  on(OrganizationActions.fetchTreeError, (state, { error }) => ({
    ...state,
    treeTableLoading: false,
    loading: false,
    tree: [],
    errorTree: error,
  })),

  on(OrganizationActions.updateGroupIdsAndUserIds, (state, { hierarchyId, groupIdsAndUserIds }) => {
    const existing = state.groupIdsAndUserIds[hierarchyId] ?? {};

    const updatedGroups = {
      ...existing,
      ...groupIdsAndUserIds,
    };

    return {
      ...state,
      groupIdsAndUserIds: {
        ...state.groupIdsAndUserIds,
        [hierarchyId]: updatedGroups,
      },
    };
  }),

  on(
    OrganizationActions.fetchGroupIdsAndUserIdsSuccess,
    (state, { hierarchyId, groupIdsAndUserIds }) => ({
      ...state,
      groupIdsAndUserIds: {
        ...state.groupIdsAndUserIds,
        [hierarchyId]: groupIdsAndUserIds,
      },
      treeTableLoading: false,
      errorTree: null,
    }),
  ),

  on(
    OrganizationActions.fetchUserIdsForGroupSuccess,
    (state, { hierarchyId, groupId, userIds }) => {
      const existing = state.groupIdsAndUserIds[hierarchyId] ?? {};

      const newGroupEntry: Record<number, number[]> = { [groupId]: userIds };

      const updatedGroups = {
        ...existing,
        ...newGroupEntry,
      };

      return {
        ...state,
        groupIdsAndUserIds: {
          ...state.groupIdsAndUserIds,
          [hierarchyId]: updatedGroups,
        },
      };
    },
  ),

  on(OrganizationActions.fetchCompanyAttributesSuccess, (state, { attributes }) => ({
    ...state,
    companyAttributes: attributes,
  })),
  on(OrganizationActions.fetchCompanyAttributesError, (state, { error }) => ({
    ...state,
    error: error,
  })),

  on(OrganizationActions.validateUser, (state) => ({
    ...state,
    validationStatus: ValidationStatusEnum.validating,
  })),
  on(OrganizationActions.validateUserSuccess, (state, { fields }) => ({
    ...state,
    fields: fields,
    validationStatus: ValidationStatusEnum.valid,
  })),
  on(OrganizationActions.validateUserFailed, (state, { fields }) => ({
    ...state,
    fields: fields,
    validationStatus: ValidationStatusEnum.invalid,
  })),
  on(OrganizationActions.validateUserError, (state, { error }) => ({
    ...state,
    validationStatus: ValidationStatusEnum.error,
  })),

  on(OrganizationActions.addUsersToGroup, (state, { hierarchyId, groupId, users }) => {
    const userIds = users.map((u) => u.id);

    const updatedUsers = updateUsers(state.users, users);
    return {
      ...mergeUserIdsIntoGroup(state, hierarchyId, groupId, userIds),
      users: updatedUsers,
    };
  }),

  on(OrganizationActions.addUserIdsToGroup, (state, { hierarchyId, groupId, userIds }) =>
    mergeUserIdsIntoGroup(state, hierarchyId, groupId, userIds),
  ),

  on(OrganizationActions.addUserSuccess, (state, { user }) => {
    const { hierarchyId, groupId, id: userId } = user;

    const updatedUsers = updateUsers(state.users, [user]);

    const currentGroups = state.groupIdsAndUserIds[hierarchyId] ?? {};
    const updatedGroups = {
      ...currentGroups,
      [groupId]: [...(currentGroups[groupId] ?? []), userId],
    };

    const { updatedGroupIdsAndUserIds, updatedTree } = updateHierarchyState(
      state,
      hierarchyId,
      updatedGroups,
    );

    return {
      ...state,
      users: updatedUsers,
      groupIdsAndUserIds: updatedGroupIdsAndUserIds,
      tree: updatedTree,
      validationStatus: ValidationStatusEnum.idle,
    };
  }),
  on(OrganizationActions.addUserError, (state, { error }) => ({
    ...state,
    error: error,
  })),

  on(OrganizationActions.editUserSuccess, (state, { user }) => ({
    ...state,
    users: updateUsers(state.users, [user]),
    validationStatus: ValidationStatusEnum.idle,
  })),
  on(OrganizationActions.editUserError, (state, { error }) => ({
    ...state,
    error: error,
  })),

  on(OrganizationActions.editUsersSuccess, (state, { users }) => ({
    ...state,
    users: updateUsers(state.users, users),
  })),
  on(OrganizationActions.editUsersError, (state, { error }) => ({
    ...state,
    error: error,
  })),

  on(OrganizationActions.createUsersSuccess, (state, { response }) => ({
    ...state,
    users: updateUsers(state.users, response.createdUsers),
  })),
  on(OrganizationActions.createUsersError, (state, { error }) => ({
    ...state,
    error: error,
  })),

  on(OrganizationActions.selectHierarchy, (state, { id }) => ({
    ...state,
    activeHierarchy: id,
  })),

  on(OrganizationActions.addRuledBasedGroup, (state) => ({
    ...state,
    validationStatus: ValidationStatusEnum.validating,
    loading: true,
    error: '',
  })),

  on(OrganizationActions.addGroup, (state) => ({
    ...state,
    validationStatus: ValidationStatusEnum.validating,
    loading: true,
    error: '',
  })),
  on(
    OrganizationActions.addGroupSuccess,
    (state, { hierarchyId, categoryId, parentId, group, leaf, manager }) => {
      const updatedTree = state.tree.map((hierarchy) => {
        if (hierarchy.id !== hierarchyId) return hierarchy;

        const updatedFlattenedTree = insertGroupIntoFlattenedTree(
          hierarchy.flattenedTree ?? [],
          group,
          categoryId,
          parentId,
          hierarchyId,
          leaf,
          manager,
        );

        return {
          ...hierarchy,
          flattenedTree: updatedFlattenedTree,
        };
      });

      const updatedGroupIdsAndUserIds = leaf
        ? {
            ...state.groupIdsAndUserIds,
            [hierarchyId]: {
              ...(state.groupIdsAndUserIds[hierarchyId] ?? {}),
              [group.id]: [],
            },
          }
        : state.groupIdsAndUserIds;

      return {
        ...state,
        tree: updatedTree,
        groupIdsAndUserIds: updatedGroupIdsAndUserIds,
        validationStatus: ValidationStatusEnum.valid,
        loading: false,
        error: '',
      };
    },
  ),

  on(OrganizationActions.addGroupValidationFail, (state, { error }) => ({
    ...state,
    loading: false,
    validationStatus: ValidationStatusEnum.invalid,
    error: error,
  })),
  on(OrganizationActions.addGroupError, (state, { error }) => ({
    ...state,
    loading: false,
    validationStatus: ValidationStatusEnum.idle,
    error: error,
  })),

  on(OrganizationActions.editRuledBasedGroup, (state) => ({
    ...state,
    loading: true,
    validationStatus: ValidationStatusEnum.validating,
    error: '',
  })),

  on(OrganizationActions.editGroup, (state) => ({
    ...state,
    loading: true,
    validationStatus: ValidationStatusEnum.validating,
    error: '',
  })),
  on(OrganizationActions.editGroupSuccess, (state, { group, manager }) => {
    const updatedTree = state.tree.map((hierarchy) => {
      if (!hierarchy.flattenedTree) return hierarchy;

      return {
        ...hierarchy,
        flattenedTree: updateNodeRecursively(hierarchy.flattenedTree, group, manager),
      };
    });

    return {
      ...state,
      validationStatus: ValidationStatusEnum.valid,
      loading: false,
      error: '',
      tree: updatedTree,
    };
  }),
  on(OrganizationActions.editGroupValidationFail, (state, { error }) => ({
    ...state,
    validationStatus: ValidationStatusEnum.invalid,
    loading: false,
    error: error,
  })),
  on(OrganizationActions.editGroupError, (state, { error }) => ({
    ...state,
    validationStatus: ValidationStatusEnum.idle,
    loading: false,
    error: error,
  })),

  on(OrganizationActions.updateValidationStatus, (state, { validationStatus }) => ({
    ...state,
    validationStatus: validationStatus,
    error: '',
  })),

  on(OrganizationActions.moveGroup, (state, { currentParentId, newParentId }) => ({
    ...state,
    loading: true,
    indicatorsLoading: [currentParentId, newParentId],
    error: '',
  })),
  on(
    OrganizationActions.moveGroupSuccess,
    (state, { groupId, hierarchyId, currentParentId, newParentId }) => {
      const updatedTree = state.tree.map((hierarchy) => {
        if (hierarchy.id !== hierarchyId || !hierarchy.flattenedTree) return hierarchy;

        const { updatedTree: treeWithoutNode, removedNode } = removeGroupImmutable(
          hierarchy.flattenedTree,
          groupId,
        );

        if (!removedNode) return hierarchy;

        const updatedNode = {
          ...removedNode,
          parentId: newParentId,
        };

        const newTree = insertGroupImmutable(treeWithoutNode, newParentId, updatedNode);

        // Recalculate user counts after moving
        const recalculatedTree = recalculateUserCounts(newTree);

        return {
          ...hierarchy,
          flattenedTree: recalculatedTree,
        };
      });

      return {
        ...state,
        loading: false,
        indicatorsLoading: [],
        error: '',
        tree: updatedTree,
      };
    },
  ),
  on(OrganizationActions.moveGroupError, (state, { error }) => ({
    ...state,
    loading: false,
    error: error,
  })),

  on(OrganizationActions.moveGroups, (state, { request }) => ({
    ...state,
    treeTableLoading: true,
    indicatorsLoading: [
      request.moveGroupInfo[0]?.moveGroup?.newParentId,
      ...request.moveGroupInfo.map((g) => g.groupId),
    ],
    error: '',
  })),

  on(OrganizationActions.fetchAllowedMoveCategoriesSuccess, (state, { allowedMoveCategories }) => ({
    ...state,
    allowedMoveCategories: allowedMoveCategories,
    error: '',
  })),
  on(OrganizationActions.fetchAllowedMoveCategoriesError, (state, { error }) => ({
    ...state,
    error: error,
  })),

  on(OrganizationActions.selectGroupRows, (state, { ids }) => ({
    ...state,
    selectedGroupRows: ids,
  })),
  on(OrganizationActions.selectRows, (state, { ids }) => ({
    ...state,
    selectedRows: ids,
  })),

  on(OrganizationActions.moveUsers, (state, { newGroupId }) => ({
    ...state,
    loading: true,
    indicatorsLoading: [newGroupId],
    error: '',
  })),
  on(
    OrganizationActions.moveUsersSuccess,
    (state, { hierarchyId, groupId, userIds, usersAfterMove }) => {
      const currentGroups = state.groupIdsAndUserIds[hierarchyId] || {};

      const updatedGroups: Record<number, number[]> = {};
      const updatedUsers = updateUsers(state.users, usersAfterMove);

      for (const [gidStr, users] of Object.entries(currentGroups)) {
        const gid = Number(gidStr);

        if (gid === groupId) {
          const newUsers = [...users];
          for (const uid of userIds) {
            if (!newUsers.includes(uid)) {
              newUsers.push(uid);
            }
          }
          updatedGroups[gid] = newUsers;
        } else {
          updatedGroups[gid] = users.filter((uid) => !userIds.includes(uid));
        }
      }

      const updatedTree = state.tree.map((h) => {
        if (h.id !== hierarchyId) return h;

        const updatedFlattenedTree = h.flattenedTree
          ? updateTreeWithUserCounts(h.flattenedTree, updatedGroups)
          : undefined;

        return {
          ...h,
          flattenedTree: updatedFlattenedTree,
        };
      });

      return {
        ...state,
        loading: false,
        indicatorsLoading: [],
        groupIdsAndUserIds: {
          ...state.groupIdsAndUserIds,
          [hierarchyId]: updatedGroups,
        },
        tree: updatedTree,
        users: updatedUsers,
      };
    },
  ),
  on(OrganizationActions.moveUsersError, (state, { error }) => ({
    ...state,
    loading: false,
    error: error,
  })),

  on(OrganizationActions.deleteSegment, (state) => ({
    ...state,
    loading: true,
    error: '',
  })),

  on(OrganizationActions.deleteGroup, (state) => ({
    ...state,
    loading: true,
    error: '',
  })),

  on(OrganizationActions.deleteTeamAndRemoveAttributes, (state) => ({
    ...state,
    loading: true,
    error: '',
  })),

  on(OrganizationActions.deleteGroupSuccess, (state, { groupId }) => {
    const updatedTree = state.tree.map((hierarchy) => {
      if (!hierarchy.flattenedTree) return hierarchy;

      const { updatedTree: treeWithoutNode, removedNode } = removeGroupImmutable(
        hierarchy.flattenedTree,
        groupId,
      );

      if (!removedNode) return hierarchy;

      const parentId = removedNode.parentId;

      let treeWithChildrenReattached = treeWithoutNode;

      if (removedNode.children && removedNode.children.length > 0) {
        // Reattach each child to the deleted group's parent
        for (const child of removedNode.children) {
          const updatedChild = { ...child, parentId };
          treeWithChildrenReattached = insertGroupImmutable(
            treeWithChildrenReattached,
            parentId,
            updatedChild,
          );
        }
      }

      const recalculatedTree = recalculateUserCounts(treeWithChildrenReattached);

      return {
        ...hierarchy,
        flattenedTree: recalculatedTree,
      };
    });

    // Remove the groupId from all hierarchies in groupIdsAndUserIds
    const updatedGroupIdsAndUserIds: Record<number, Record<number, number[]>> = {};

    for (const [hierarchyId, groups] of Object.entries(state.groupIdsAndUserIds)) {
      const { [groupId]: removedGroup, ...remainingGroups } = groups;
      updatedGroupIdsAndUserIds[Number(hierarchyId)] = remainingGroups;
    }

    return {
      ...state,
      loading: false,
      error: '',
      tree: updatedTree,
      groupIdsAndUserIds: updatedGroupIdsAndUserIds,
    };
  }),
  on(OrganizationActions.deleteGroupError, (state, { error }) => ({
    ...state,
    loading: false,
    error: error,
  })),

  on(OrganizationActions.deleteUsers, (state) => ({
    ...state,
    loading: true,
    error: '',
  })),
  on(OrganizationActions.deleteUsersSuccess, (state, { userIds, hierarchyId }) => {
    const updatedUsers = { ...state.users };
    userIds.forEach((userId) => delete updatedUsers[userId]);

    const currentGroups = state.groupIdsAndUserIds[hierarchyId] ?? {};
    const updatedGroups: Record<number, number[]> = {};

    for (const [groupId, ids] of Object.entries(currentGroups)) {
      // Always keep group, even if empty
      updatedGroups[Number(groupId)] = ids.filter((id) => !userIds.includes(id));
    }

    const { updatedGroupIdsAndUserIds, updatedTree } = updateHierarchyState(
      state,
      hierarchyId,
      updatedGroups,
    );

    return {
      ...state,
      users: updatedUsers,
      groupIdsAndUserIds: updatedGroupIdsAndUserIds,
      tree: updatedTree,
      loading: false,
      error: '',
      validationStatus: ValidationStatusEnum.idle,
    };
  }),
  on(OrganizationActions.deleteUsersError, (state, { error }) => ({
    ...state,
    loading: false,
    error: error,
  })),

  on(OrganizationActions.setLoadingIndicatorsForRows, (state, { rowIds }) => ({
    ...state,
    indicatorsLoading: rowIds,
  })),

  on(OrganizationActions.clearError, (state) => ({
    ...state,
    organizationTableLoading: false,
    userTableLoading: false,
    treeTableLoading: false,
    loading: false,
    error: '',
    errorTree: '',
    indicatorsLoading: [],
  })),

  on(OrganizationActions.setActiveSwitchOption, (state, { option }) => ({
    ...state,
    activeSwitchOption: option,
  })),

  on(OrganizationActions.setSearchString, (state, { instanceId, searchstring }) => ({
    ...state,
    instanceStates: {
      ...state.instanceStates,
      [instanceId]: {
        ...state.instanceStates[instanceId],
        searchstring: searchstring,
      },
    },
  })),

  on(OrganizationActions.importSegmentUsersSuccess, (state, { groupId, hierarchyId, users }) => {
    const updatedUsers = updateUsers(
      state.users,
      users.filter((u) => !state.users[u.id]),
    );

    const currentGroups = state.groupIdsAndUserIds[hierarchyId] ?? {};
    const updatedGroups = {
      ...currentGroups,
      [groupId]: users.map((u) => u.id),
    };

    const { updatedGroupIdsAndUserIds, updatedTree } = updateHierarchyState(
      state,
      hierarchyId,
      updatedGroups,
    );

    return {
      ...state,
      groupIdsAndUserIds: updatedGroupIdsAndUserIds,
      users: updatedUsers,
      tree: updatedTree,
    };
  }),

  on(OrganizationActions.resetState, () => ({
    ...initialState,
  })),

  on(OrganizationActions.removeUsers, (state, { userIds, hierarchyId, groupId }) => {
    // This reducer only removes users from group, the users are not deleted.
    const currentGroups = state.groupIdsAndUserIds[hierarchyId] ?? {};
    const currentGroupUserIds = currentGroups[groupId] ?? [];

    const updatedGroups: Record<number, number[]> = {
      ...currentGroups,
      [groupId]: currentGroupUserIds.filter((id) => !userIds.includes(id)),
    };

    const { updatedGroupIdsAndUserIds, updatedTree } = updateHierarchyState(
      state,
      hierarchyId,
      updatedGroups,
    );

    return {
      ...state,
      groupIdsAndUserIds: updatedGroupIdsAndUserIds,
      tree: updatedTree,
      loading: false,
      error: '',
      validationStatus: ValidationStatusEnum.idle,
    };
  }),

  on(OrganizationActions.setActiveInstance, (state, { instanceId }) => {
    return {
      ...state,
      activeInstance: instanceId,
    };
  }),

  on(OrganizationActions.removeInstanceState, (state, { instanceId }) => {
    const { [instanceId]: removed, ...remaining } = state.instanceStates;

    // Get all keys in insertion order
    const keys = Object.keys(remaining);

    // Get the last key if it exists, otherwise null
    const lastKey = keys.length > 0 ? keys[keys.length - 1] : null;

    return {
      ...state,
      instanceStates: remaining,
      activeInstance: lastKey,
    };
  }),

  on(
    OrganizationActions.setActiveActionBarInstance,
    (state, { instanceId, componentInstanceId }) => ({
      ...state,
      instanceStates: {
        ...state.instanceStates,
        [instanceId]: {
          ...state.instanceStates[instanceId],
          activeActionBar: componentInstanceId,
        },
      },
    }),
  ),
);

function updateHierarchyState(
  state: OrganizationState,
  hierarchyId: number,
  updatedGroups: Record<number, number[]>,
): { updatedGroupIdsAndUserIds: typeof state.groupIdsAndUserIds; updatedTree: typeof state.tree } {
  const updatedGroupIdsAndUserIds = {
    ...state.groupIdsAndUserIds,
    [hierarchyId]: updatedGroups,
  };

  const updatedTree = state.tree.map((h) => {
    if (h.id !== hierarchyId) return h;

    const updatedFlattenedTree = h.flattenedTree
      ? updateTreeWithUserCounts(h.flattenedTree, updatedGroups)
      : undefined;

    return {
      ...h,
      flattenedTree: updatedFlattenedTree,
    };
  });

  return { updatedGroupIdsAndUserIds, updatedTree };
}

function insertGroupIntoFlattenedTree(
  flattened: FlattenedTreeModel<OrganisationGroupModel>[],
  group: Group,
  categoryId: number,
  parentId: number,
  hierarchyId: number,
  leaf: boolean,
  manager: { id: number; name: string; email: string },
): FlattenedTreeModel<OrganisationGroupModel>[] {
  const newNode: FlattenedTreeModel<OrganisationGroupModel> = {
    id: group.id,
    name: group.name,
    category: group.category,
    categoryId: categoryId,
    hierarchyId: hierarchyId,
    categoryName: group.categoryName,
    externalId: group.externalId,
    userCount: group.userCount ?? 0,
    managerName: group.managerNames,
    managerEmail: manager?.email,
    managerExternalId: manager?.email,
    reportAccessUserNames: manager?.name,
    isPin: group.isPin ?? false,
    parentId: parentId,
    hasChildren: false,
    children: [],
    leaf: leaf,
    categorySortPriority: leaf ? 2 : 1,
  };

  if (!parentId) {
    // New root-level node
    return [...flattened, newNode];
  }

  return flattened.map((node) => {
    if (node.id === parentId) {
      const updatedChildren = [...node.children, newNode];
      return {
        ...node,
        children: updatedChildren,
        hasChildren: true,
      };
    }

    if (node.children?.length) {
      return {
        ...node,
        children: insertGroupIntoFlattenedTree(
          node.children,
          group,
          categoryId,
          parentId,
          hierarchyId,
          leaf,
          manager,
        ),
      };
    }

    return node;
  });
}

function removeGroupImmutable(
  nodes: FlattenedTreeModel<OrganisationGroupModel>[],
  targetId: number,
): {
  updatedTree: FlattenedTreeModel<OrganisationGroupModel>[];
  removedNode: FlattenedTreeModel<OrganisationGroupModel> | null;
} {
  let removedNode: FlattenedTreeModel<OrganisationGroupModel> | null = null;

  const updatedTree = nodes
    .map((node) => {
      if (node.id === targetId) {
        removedNode = node;
        return null;
      }

      const { updatedTree: newChildren, removedNode: childRemoved } = removeGroupImmutable(
        node.children ?? [],
        targetId,
      );

      if (childRemoved) {
        removedNode = childRemoved;
      }

      return {
        ...node,
        children: newChildren,
      };
    })
    .filter((n): n is FlattenedTreeModel<OrganisationGroupModel> => n !== null);

  return { updatedTree, removedNode };
}

function insertGroupImmutable(
  nodes: FlattenedTreeModel<OrganisationGroupModel>[],
  parentId: number,
  newNode: FlattenedTreeModel<OrganisationGroupModel>,
): FlattenedTreeModel<OrganisationGroupModel>[] {
  return nodes.map((node) => {
    if (node.id === parentId) {
      return {
        ...node,
        children: [...(node.children ?? []), newNode],
      };
    }

    return {
      ...node,
      children: insertGroupImmutable(node.children ?? [], parentId, newNode),
    };
  });
}

function updateNodeRecursively(
  nodes: FlattenedTreeModel<OrganisationGroupModel>[],
  group: Group,
  manager: { id: number; name: string; email: string },
): FlattenedTreeModel<OrganisationGroupModel>[] {
  return nodes.map((node) => {
    // If it's the node to update, return a new object with the updates
    if (node.id === group.id) {
      const updatedNode = {
        ...node,
        name: group.name,
        externalId: group.externalId,
        userCount: group.userCount,
        isPin: group.isPin ?? node.isPin,
        managerName: group.managerNames,
        managerEmail: manager?.email,
        managerExternalId: manager?.email,
        reportAccessUserNames: manager?.name,
      };

      return {
        ...updatedNode,
        children: updateNodeRecursively(node.children ?? [], group, manager),
      };
    }

    // Recurse into children
    return {
      ...node,
      children: updateNodeRecursively(node.children ?? [], group, manager),
    };
  });
}

function recalculateUserCounts(
  nodes: FlattenedTreeModel<OrganisationGroupModel>[],
): FlattenedTreeModel<OrganisationGroupModel>[] {
  return nodes.map((node) => {
    const updatedChildren = node.children ? recalculateUserCounts(node.children) : [];

    let updatedUserCount: number;

    if (updatedChildren.length === 0) {
      // node withouth childreen: keep original userCount
      updatedUserCount = node.leaf ? (node.userCount ?? 0) : 0;
    } else {
      // Parent node: sum immediate children's userCounts
      updatedUserCount = updatedChildren.reduce((sum, child) => sum + (child.userCount ?? 0), 0);
    }

    return {
      ...node,
      userCount: updatedUserCount,
      children: updatedChildren,
    };
  });
}

function updateTreeWithUserCounts(
  nodes: FlattenedTreeModel<OrganisationGroupModel>[],
  userMap: Record<number, number[]>,
): FlattenedTreeModel<OrganisationGroupModel>[] {
  const updateCounts = (
    tree: FlattenedTreeModel<OrganisationGroupModel>[],
  ): FlattenedTreeModel<OrganisationGroupModel>[] => {
    return tree.map((node) => {
      const updatedChildren = node.children ? updateCounts(node.children) : [];

      const groupId = node.id;
      // Always recompute from userMap, fallback = 0
      const directUserCount = userMap[groupId]?.length ?? 0;

      let totalUserCount: number;
      if (updatedChildren.length === 0) {
        totalUserCount = node.leaf ? directUserCount : 0;
      } else {
        totalUserCount = updatedChildren.reduce((sum, child) => sum + (child.userCount ?? 0), 0);
      }

      return {
        ...node,
        userCount: totalUserCount,
        children: updatedChildren,
      };
    });
  };

  return updateCounts(nodes);
}

function updateUsers(stateUsers: Record<number, User>, updatedUsers: User[]) {
  const newUserState = { ...stateUsers };
  updatedUsers.forEach((user) => (newUserState[user.id] = user));
  return newUserState;
}
function mergeUserIdsIntoGroup(
  state: OrganizationState,
  hierarchyId: number,
  groupId: number,
  userIds: number[],
): OrganizationState {
  if (userIds.length === 0) return state;

  const currentGroups = state.groupIdsAndUserIds[hierarchyId] ?? {};

  const mergedUserIds = Array.from(new Set([...(currentGroups[groupId] ?? []), ...userIds]));

  const updatedGroups = {
    ...currentGroups,
    [groupId]: mergedUserIds,
  };

  const { updatedGroupIdsAndUserIds, updatedTree } = updateHierarchyState(
    state,
    hierarchyId,
    updatedGroups,
  );

  return {
    ...state,
    groupIdsAndUserIds: updatedGroupIdsAndUserIds,
    tree: updatedTree,
    validationStatus: ValidationStatusEnum.idle,
  };
}
